import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiClient } from './api/client';
import { ApiClientContext } from './api/apiContext';
import { AppShell } from './components/AppShell';
import { RequirePermission } from './components/RequirePermission';
import { LoadingState, NotFoundState } from './components/states/StateViews';
import { useAuthStore } from './auth/authStore';
import { sessionTokenStore } from './auth/sessionPersistence';
import { readConfig } from './config/env';
import { isReleased } from './config/release';
import { NAVIGATION } from './navigation/navigation';
import { AuditPage } from './routes/AuditPage';
import { BranchesPage } from './routes/BranchesPage';
import { AftercarePage } from './routes/clinical/AftercarePage';
import { ConsentsPage } from './routes/clinical/ConsentsPage';
import { BrandingPage } from './routes/BrandingPage';
import { CatalogPage } from './routes/CatalogPage';
import { ChangePasswordPage } from './routes/ChangePasswordPage';
import { InventoryPage } from './routes/inventory/InventoryPage';
import { MachinesPage } from './routes/machines/MachinesPage';
import { NotificationsPage } from './routes/NotificationsPage';
import { CheckoutPage } from './routes/money/CheckoutPage';
import { DayBoardPage } from './routes/DayBoardPage';
import { DoctorHoursPage } from './routes/doctorHours/DoctorHoursPage';
import { HomePage } from './routes/HomePage';
import { PackagesPage } from './routes/PackagesPage';
import { PatientsPage } from './routes/PatientsPage';
import { PlaceholderPage } from './routes/PlaceholderPage';
import { PoliciesPage } from './routes/PoliciesPage';
import { ExpensesPage } from './routes/money/ExpensesPage';
import { RefundsPage } from './routes/money/RefundsPage';
import { SalesPage } from './routes/money/SalesPage';
import { SignInPage } from './routes/SignInPage';
import { StaffPage } from './routes/StaffPage';
import { SupportAccessPage } from './routes/SupportAccessPage';
import { WorklistsPage } from './routes/WorklistsPage';
import { AppointmentsPage } from './routes/AppointmentsPage';
import { PlatformClinicsPage } from './routes/platform/PlatformClinicsPage';
import { PlatformFeatureFlagsPage } from './routes/platform/PlatformFeatureFlagsPage';
import { PlatformInvoicesPage } from './routes/platform/PlatformInvoicesPage';
import { PlatformSupportAccessPage } from './routes/platform/PlatformSupportAccessPage';
import { ReportsPage } from './routes/insight/ReportsPage';
import { StatementsPage } from './routes/money/StatementsPage';
import { BroadcastsPage } from './routes/comms/BroadcastsPage';
import { ChatPage } from './routes/comms/ChatPage';

/** Navigation keys whose screens exist. Anything absent still renders its milestone placeholder. */
const SCREENS: Readonly<Record<string, () => JSX.Element>> = {
  dayBoard: DayBoardPage,
  appointments: AppointmentsPage,
  patients: PatientsPage,
  catalog: CatalogPage,
  packages: PackagesPage,
  policies: PoliciesPage,
  checkout: CheckoutPage,
  sales: SalesPage,
  expenses: ExpensesPage,
  refunds: RefundsPage,
  branches: BranchesPage,
  branding: BrandingPage,
  notifications: NotificationsPage,
  consents: ConsentsPage,
  aftercare: AftercarePage,
  staff: StaffPage,
  doctorHours: DoctorHoursPage,
  supportGrants: SupportAccessPage,
  audit: AuditPage,
  tenants: PlatformClinicsPage,
  invoices: PlatformInvoicesPage,
  featureFlags: PlatformFeatureFlagsPage,
  platformSupport: PlatformSupportAccessPage,
  worklists: WorklistsPage,
  inventory: InventoryPage,
  machines: MachinesPage,
  reports: ReportsPage,
  statements: StatementsPage,
  chat: ChatPage,
  broadcasts: BroadcastsPage,
  platformAudit: () => <AuditPage scope="platform" />,
};

/**
 * The application shell: one API client, one query client, one route tree.
 *
 * Routes are generated from the same navigation map that builds the sidebar, so a destination can
 * never exist in the menu without a route or vice versa, and both derive their permissions from
 * one declaration.
 */
export function App({ apiClient }: { readonly apiClient?: ApiClient }) {
  const { i18n } = useTranslation();
  const localeRef = useRef(i18n.language);
  localeRef.current = i18n.language;

  const client = useMemo(
    () =>
      apiClient ??
      new ApiClient({
        baseUrl: readConfig().apiBaseUrl,
        currentLocale: () => localeRef.current,
        onSessionExpired: () => useAuthStore.getState().sessionExpired(),
        tokenStore: sessionTokenStore,
      }),
    [apiClient],
  );

  // A reload brings the tokens back but not who they belong to. Until /me answers, nothing is
  // routed, so a signed-in user is not bounced through the sign-in page on the way back.
  const [restoring, setRestoring] = useState(
    () => client.isAuthenticated && useAuthStore.getState().phase === 'anonymous',
  );
  useEffect(() => {
    if (restoring) {
      void useAuthStore
        .getState()
        .restore(client)
        .finally(() => setRestoring(false));
    }
  }, [client, restoring]);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Clinic data changes under the user's feet all day; a stale read is worse than a
            // refetch. Retries stay low so a genuine failure surfaces quickly rather than after
            // three silent attempts.
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: true,
          },
        },
      }),
    [],
  );

  return (
    <ApiClientContext.Provider value={client}>
      <QueryClientProvider client={queryClient}>
        {restoring ? (
          <div className="centered-page">
            <LoadingState />
          </div>
        ) : (
          <Routes>
            <Route path="/sign-in" element={<SignInPage />} />

            <Route
              element={
                <RequirePermission>
                  <AppShell />
                </RequirePermission>
              }
            >
              <Route index element={<HomePage />} />
              {/* The viewer's own account, reached from beside their name rather than from the menu:
                  every role has one, so it needs nothing beyond being signed in. */}
              <Route path="/account/password" element={<ChangePasswordPage />} />
              {/* An unreleased destination gets no route at all, so typing its address lands on 404
                  rather than on a screen the sidebar is keeping out of sight (config/release.ts). */}
              {NAVIGATION.flatMap((group) =>
                group.items.filter((item) => isReleased(item.key)).map((item) => {
                  // A destination whose milestone has landed renders its screen; the rest keep the
                  // placeholder that names the milestone bringing them. One map, so the sidebar, the
                  // route table and the permissions can never disagree.
                  const Screen = SCREENS[item.key] ?? PlaceholderPage;
                  return (
                    <Route
                      key={item.key}
                      path={item.path}
                      element={
                        <RequirePermission requires={item.requires}>
                          <Screen />
                        </RequirePermission>
                      }
                    />
                  );
                }),
              )}
            </Route>

            <Route path="/404" element={<NotFoundState />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        )}
      </QueryClientProvider>
    </ApiClientContext.Provider>
  );
}
